import React, { useState } from "react";
import "../../../style/dashboard/user-tenant-management.module.css";
import "../../../style/dashboard/common.module.css";

function Header() {
    return (
        <div className="utm-header-container user-tenant-management-paper user-tenant-management-section">
            <div className="utm-header-logo-container">
                <img src="/static/assets/dashboard/logo.png" alt="logo" />
            </div>
            <div className="utm-header-content-container">
                <h1>User Tenant Management</h1>
                <p>Manage your users and tenants effortlessly with our user and tenant management dashboard</p>
            </div>
        </div>
    );
}

function ArchitectureTabs() {
    const [selectedTab, setSelectedTab] = useState<"managed-service" | "self-hosted">("managed-service");
    return (
        <div className="user-tenant-management-architecture-container">
            <div className="user-tenant-management-architecture-tabs">
                <span
                    className={selectedTab === "managed-service" ? "selected" : ""}
                    onClick={() => setSelectedTab("managed-service")}
                >
                    Managed Service
                </span>
                <span
                    className={selectedTab === "self-hosted" ? "selected" : ""}
                    onClick={() => setSelectedTab("self-hosted")}
                >
                    Self Hosted
                </span>
            </div>
            <div className="user-tenant-management-architecture-content">
                <img
                    src="/static/assets/dashboard/managed_service_generic.png"
                    alt="managed service architecture"
                    style={{ display: selectedTab === "managed-service" ? "block" : "none" }}
                />
                <img
                    src="/static/assets/dashboard/self_hosted_generic.png"
                    alt="self hosted architecture"
                    style={{ display: selectedTab === "self-hosted" ? "block" : "none" }}
                />
            </div>
        </div>
    );
}

interface StepProps {
    stepNumber: number;
    stepDescription: string;
}

function Step({ stepNumber, stepDescription }: StepProps) {
    return (
        <div className="user-tenant-management-step spacer">
            <span>Step {stepNumber}:</span> <span>{stepDescription}</span>
        </div>
    );
}

export default function UserTenantManagement() {
    return (
        <div className="user-tenant-management-container">
            <div className="centered-box">
                <Header />
                <div className="user-tenant-management-instructions user-tenant-management-section">
                    <h1 className="user-tenant-management-heading">Get started!</h1>
                    <p className="user-tenant-management-description">
                        Follow the steps below to get access to your user and tenant management dashboard.
                    </p>
                </div>
                <div className="user-tenant-management-section user-tenant-management-paper">
                    <h2>Architecture</h2>
                    <ArchitectureTabs />
                </div>

                <div className="user-tenant-management-section user-tenant-management-paper">
                    <Step stepNumber={1} stepDescription="Complete quick setup" />
                    <div className="user-tenant-management-description spacer">
                        <p className="spacer">
                            To get started, you need to complete the quick setup. This will allow you to create a new
                            tenant and manage your users and tenants.
                        </p>
                        <div className="announcement-container no-bottom-space">
                            <span className="announcement-blue-pill centered">IMPORTANT</span>
                            <div className="announcement-content">
                                Setting up the SuperTokens backend SDK is crucial for serving the dashboard.{" "}
                                <a href="https://supertokens.com/docs/guides" target="_blank">
                                    Click here
                                </a>{" "}
                                to access all recipes and follow the vital quick setup guide to ensure your SuperTokens
                                backend SDK is configured properly.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="user-tenant-management-section user-tenant-management-paper">
                    <Step stepNumber={2} stepDescription="Initialise the dashboard recipe" />
                    <div className="user-tenant-management-description spacer">
                        <p className="spacer">Add the Dashboard recipe to your backend SDK.</p>
                        <div className="announcement-container no-bottom-space">
                            <span className="announcement-blue-pill centered">INFO</span>
                            <div className="announcement-content">
                                <a
                                    href="https://supertokens.com/docs/userdashboard/about#initialise-the-dashboard-recipe"
                                    target="_blank"
                                >
                                    Click here
                                </a>{" "}
                                to see documentation on how to initialise the dashboard recipe for your desired
                                framework.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="user-tenant-management-section user-tenant-management-paper">
                    <Step stepNumber={3} stepDescription="Access the Dashboard" />

                    <div className="announcement-container no-bottom-space" style={{ marginTop: "20px" }}>
                        <span className="announcement-blue-pill centered">IMPORTANT</span>
                        <div className="announcement-content">
                            The user management dashboard is served by the backend SDK, you have to use your API domain
                            when trying to visit the dashboard.
                        </div>
                    </div>
                    <div className="user-tenant-management-highlight">
                        <div className="user-tenant-management-highlight-content">
                            <span>Navigate to</span>{" "}
                            <span className="highlight-purple">YOUR_API_DOMAIN/auth/dashboard</span>{" "}
                            <span>to view the dashboard.</span>
                        </div>
                    </div>
                    <div className="user-tenant-management-description spacer">
                        For <b>Next.js</b> users, the dashboard will be accessible at{" "}
                        <b className="highlight-grey">YOUR_API_DOMAIN/api/auth/dashboard</b>
                    </div>
                    <div className="divider"></div>
                    <div className="user-tenant-management-content-footer">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M10 12.5C10.663 12.5 11.2989 12.2366 11.7678 11.7678C12.2366 11.2989 12.5 10.663 12.5 10C12.5 9.33696 12.2366 8.70107 11.7678 8.23223C11.2989 7.76339 10.663 7.5 10 7.5C9.33696 7.5 8.70107 7.76339 8.23223 8.23223C7.76339 8.70107 7.5 9.33696 7.5 10C7.5 10.663 7.76339 11.2989 8.23223 11.7678C8.70107 12.2366 9.33696 12.5 10 12.5Z"
                                fill="#007AFF"
                            />
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M0.664859 10.59C0.517964 10.2083 0.517964 9.78569 0.664859 9.404C1.39209 7.51849 2.67331 5.89744 4.33982 4.75431C6.00633 3.61117 7.97997 2.99956 10.0009 3C14.2579 3 17.8939 5.66 19.3369 9.41C19.4839 9.791 19.4829 10.214 19.3369 10.596C18.6096 12.4815 17.3284 14.1026 15.6619 15.2457C13.9954 16.3888 12.0218 17.0004 10.0009 17C5.74386 17 2.10786 14.34 0.664859 10.59ZM14.0009 10C14.0009 11.0609 13.5794 12.0783 12.8293 12.8284C12.0791 13.5786 11.0617 14 10.0009 14C8.93999 14 7.92258 13.5786 7.17243 12.8284C6.42229 12.0783 6.00086 11.0609 6.00086 10C6.00086 8.93913 6.42229 7.92172 7.17243 7.17157C7.92258 6.42143 8.93999 6 10.0009 6C11.0617 6 12.0791 6.42143 12.8293 7.17157C13.5794 7.92172 14.0009 8.93913 14.0009 10Z"
                                fill="#007AFF"
                            />
                        </svg>
                        <a
                            href="https://supertokens.com/docs/userdashboard/about#viewing-the-dashboard"
                            target="_blank"
                        >
                            See Documentation
                        </a>
                    </div>
                </div>

                <div className="user-tenant-management-section user-tenant-management-paper">
                    <Step stepNumber={4} stepDescription="Create Dashboard Credentials" />
                    <div className="user-tenant-management-description spacer">
                        <p className="spacer">
                            When you first setup SuperTokens, there are no credentials created for the dashboard.
                        </p>
                        <div className="announcement-container no-bottom-space">
                            <span className="announcement-blue-pill centered">INFO</span>
                            <div className="announcement-content">
                                <a
                                    href="https://supertokens.com/docs/userdashboard/about#creating-dashboard-credentials"
                                    target="_blank"
                                >
                                    Click here
                                </a>{" "}
                                to see detailed documentation on how to make a request to SuperTokens core to create
                                credentials and access the dashboard.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="user-tenant-management-section user-tenant-management-paper">
                    <Step stepNumber={5} stepDescription="Log in! &nbsp;🎉" />
                    <div className="user-tenant-management-description spacer">
                        <p className="spacer">Use the credentials to log in to the dashboard.</p>
                    </div>
                </div>
                <div className="announcement-container no-bottom-space">
                    <span className="announcement-blue-pill centered">INFO</span>
                    <div className="announcement-content">
                        <a
                            href="https://supertokens.com/docs/userdashboard/about#updating-dashboard-credentials"
                            target="_blank"
                        >
                            Click here
                        </a>{" "}
                        to see detailed documentation on how Update Dashboard Credentials, Restricting access to
                        dashboard users etc.
                    </div>
                </div>
            </div>
        </div>
    );
}
